import { useCallback, useEffect, useState } from "react";
import { Megaphone, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { broadcastApi, type BroadcastAudience, type BroadcastItem } from "../../lib/broadcastApi";
import { PageHeader } from "../../components/ui/PageHeader";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { EmptyStateCard } from "../../components/ui/EmptyStateCard";
import { SkeletonList } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";
import { mapApiError } from "../../components/ui/StatusMessage";

const MAX = 1500;
const AUDIENCES: { id: BroadcastAudience; label: string }[] = [
  { id: "all", label: "All students" },
  { id: "premium", label: "Premium" },
  { id: "free", label: "Free" },
];

export function AdminBroadcastPage() {
  const { getAccessToken } = useAuth();
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState<BroadcastAudience>("all");
  const [items, setItems] = useState<BroadcastItem[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      setItems((await broadcastApi.list(token)).items);
    } catch (e) {
      setItems([]);
      toast.error(mapApiError(e).message);
    }
  }, [getAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // While a broadcast is still sending, refresh the counts.
  useEffect(() => {
    if (!items?.some((b) => b.status !== "done")) return;
    const id = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(id);
  }, [items, load]);

  async function send() {
    setSending(true);
    try {
      const token = await getAccessToken();
      if (!token) return;
      const b = await broadcastApi.send(token, message.trim(), audience);
      toast.success(`Sending to ${b.total} students on Telegram.`);
      setMessage("");
      setConfirm(false);
      await load();
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => setConfirm(true) } });
    } finally {
      setSending(false);
    }
  }

  const ready = message.trim().length >= 3;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <PageHeader title="Broadcast" description="Send a message to students. They receive it on Telegram only." />

      <Card className="space-y-4 p-4 sm:p-5">
        <div>
          <label htmlFor="bc-message" className="mb-1 block text-sm font-semibold text-foreground">Message</label>
          <textarea
            id="bc-message"
            value={message}
            maxLength={MAX}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            placeholder="Write a short, clear message."
            className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm"
          />
          <p className="mt-1 text-right text-xs text-muted-foreground">{message.length}/{MAX}</p>
        </div>
        <div role="group" aria-label="Who receives it" className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground">Send to</span>
          {AUDIENCES.map((a) => (
            <button
              key={a.id}
              type="button"
              aria-pressed={audience === a.id}
              onClick={() => setAudience(a.id)}
              className={`min-h-9 rounded-full border px-3 text-sm font-semibold focus-visible:ring-2 ring-ring ${
                audience === a.id ? "border-primary bg-accent-soft text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button icon={<Send size={16} />} disabled={!ready} onClick={() => setConfirm(true)}>
            Review and send
          </Button>
        </div>
      </Card>

      <section className="space-y-3" aria-labelledby="bc-history">
        <h2 id="bc-history" className="text-lg font-bold font-display">Recent broadcasts</h2>
        {items === null ? (
          <SkeletonList count={3} />
        ) : items.length === 0 ? (
          <EmptyStateCard icon={Megaphone} title="No broadcasts yet" message="Messages you send will be listed here." />
        ) : (
          <ul className="space-y-2">
            {items.map((b) => (
              <li key={b.id}>
                <Card className="space-y-1 p-4">
                  <p className="line-clamp-2 text-sm text-foreground">{b.message}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant={b.status === "done" ? "success" : "info"}>{b.status === "done" ? "Sent" : "Sending"}</Badge>
                    <span>{b.sent}/{b.total} delivered{b.failed ? `, ${b.failed} could not be reached` : ""}</span>
                    <span>{AUDIENCES.find((a) => a.id === b.audience)?.label}</span>
                    {b.createdAt && <span>{new Date(b.createdAt).toLocaleString()}</span>}
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        isOpen={confirm}
        title="Send this message?"
        message={`It goes to ${AUDIENCES.find((a) => a.id === audience)?.label.toLowerCase()} on Telegram and cannot be taken back.`}
        confirmText="Send"
        loading={sending}
        onConfirm={send}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}
